// PowerBIHelpSection.tsx
import React, { useMemo } from "react";
import {
  Card,
  Stack,
  Title,
  Text,
  List,
  Code,
  Group,
  Button,
  Tooltip,
  CopyButton,
  Divider,
  Alert,
} from "@mantine/core";

const CopyBtn: React.FC<{ value: string; label?: string }> = ({ value, label = "Kopier" }) => (
  <CopyButton value={value} timeout={1200}>
    {({ copied, copy }) => (
      <Tooltip label={copied ? "Kopiert!" : label} withinPortal>
        <Button
          variant="secondary"
          size="sm"
          onClick={copy}
          leftSection={<i className="fa fa-copy fa-fw" />}
          style={{ minWidth: 110 }}
        >
          {copied ? "Kopiert" : label}
        </Button>
      </Tooltip>
    )}
  </CopyButton>
);

const BlockRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack gap={6} mt="xs">
    <Text size="sm" c="dimmed">
      {label}
    </Text>
    <Group justify="space-between" wrap="nowrap" gap="sm" align="flex-start">
      <Code block style={{ flex: 1, overflowX: "auto", whiteSpace: "pre" }}>
        {value}
      </Code>
      <CopyBtn value={value} />
    </Group>
  </Stack>
);

const PowerBIHelpSection: React.FC<{ url: string }> = ({ url }) => {
  // Lägg till ?apiKey=<DIN_API_NØKKEL> till URL:en
  const apiUrl = useMemo(() => {
    const baseUrl = url.split("?")[0];
    return `${baseUrl}?apiKey=<DIN_API_NØKKEL>`;
  }, [url]);

  return (
    <Card withBorder radius="lg" shadow="sm">
      <Stack gap="md">
        <Group gap="xs">
          <i className="fa fa-plug fa-fw" />
          <Title order={5} m={0}>
            Power BI – 3 enkle steg
          </Title>
        </Group>

        <Alert variant="light" color="blue" icon={<i className="fa fa-info-circle" />}>
          <Text size="sm" fw={600}>
            Erstatt <Code>&lt;DIN_API_NØKKEL&gt;</Code> med din personlige API-nøkkel først!
          </Text>
        </Alert>

        <List size="sm" withPadding spacing="xs">
          <List.Item>
            <b>1.</b> Åpne <b>Power BI Desktop</b> → <b>Hent data</b> → <b>Web</b>
          </List.Item>
          <List.Item>
            <b>2.</b> Lim inn denne URL:en:
            <BlockRow label="Komplett URL" value={apiUrl} />
          </List.Item>
          <List.Item>
            <b>3.</b> Klikk <b>OK</b> → <b>Organisasjon</b> → <b>FERDIG!</b>
          </List.Item>
        </List>

        <Divider />

        <Alert variant="light" color="green" icon={<i className="fa fa-check-circle" />}>
          <Text size="sm">
            <b>✅ Det var alt!</b> Data lastes automatisk hver gang rapporten oppdateres.
          </Text>
        </Alert>

        <Alert variant="light" color="yellow" icon={<i className="fa fa-lock" />}>
          <Text size="sm">
            <b>Sikkerhet:</b> Nøkkelen vises ikke i rapporten. Ved rotasjon: generer ny nøkkel og oppdater Power BI.
          </Text>
        </Alert>
      </Stack>
    </Card>
  );
};

export default PowerBIHelpSection;
